/**
 * Keeps the queue moving: sends what is waiting as soon as there is a network
 * again, and asks the service worker to finish the job in the background when
 * the browser supports it.
 *
 * Page-side only — this is the module that is allowed to touch `window`.
 */

import { dropOtherUsers } from './cache';
import { onOutboxChanged, processOutbox, CHANNEL_NAME } from './outbox';
import { setOfflineState, refreshOfflineState, getOfflineState } from './store';
import { fetchTransport, setApiBase, setAuthToken, setUserId } from './transport';
import type { StaleNotice } from './types';

export const SYNC_TAG = 'staketory-outbox';
const TOKEN_KEY = 'staketory_token';
const USER_KEY = 'staketory_user';
const POLL_MS = 60_000;

let started = false;
let pollTimer: ReturnType<typeof setInterval> | null = null;
let currentUserId: string | undefined;

const readLocal = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

function readUserId(): string | undefined {
  const raw = readLocal(USER_KEY);
  if (!raw) return undefined;
  try {
    const user = JSON.parse(raw);
    return user?.id ?? user?.userId ?? user?.sub ?? undefined;
  } catch {
    return undefined;
  }
}

/**
 * Mirror who is signed in into IndexedDB so background sync can send their
 * queue without the page being open, and so a shared phone never serves one
 * officer the cached directory of another.
 */
export async function syncCredentials(apiBase: string): Promise<string | undefined> {
  const token = readLocal(TOKEN_KEY);
  const userId = readUserId();
  await setApiBase(apiBase);
  await setAuthToken(token);
  await setUserId(userId ?? null);
  if (userId !== currentUserId) {
    currentUserId = userId;
    await dropOtherUsers(userId);
  }
  return userId;
}

/** Send everything that is due. Safe to call often; the queue holds a lock. */
export async function syncNow(): Promise<void> {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    setOfflineState({ online: false });
    return;
  }
  if (getOfflineState().syncing) return;

  setOfflineState({ syncing: true });
  try {
    const outcome = await processOutbox({
      transport: fetchTransport,
      userId: currentUserId,
    });
    if (outcome.sent > 0) setOfflineState({ online: true });
  } catch {
    /* the queue keeps its own per-item error; nothing to add here */
  } finally {
    setOfflineState({ syncing: false });
    await refreshOfflineState();
  }
}

/** Ask the browser to finish the queue later, even if the app is closed. */
export async function requestBackgroundSync(): Promise<boolean> {
  try {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return false;
    const registration: any = await navigator.serviceWorker.ready;
    if (!registration?.sync?.register) return false;
    await registration.sync.register(SYNC_TAG);
    return true;
  } catch {
    return false;
  }
}

function handleMessage(event: MessageEvent): void {
  const data = event?.data;
  if (!data || typeof data !== 'object') return;
  if (data.type === 'stale-response') {
    const notice: StaleNotice = {
      url: data.url,
      kind: data.kind ?? 'unknown',
      cachedAt: data.cachedAt,
      at: Date.now(),
    };
    setOfflineState({ stale: notice, online: false });
  }
  if (data.type === 'outbox-changed' || data.type === 'sync-complete') {
    void refreshOfflineState();
  }
  if (data.type === 'update-ready') {
    setOfflineState({ updateReady: true });
  }
}

/**
 * Start the offline runtime. Called once from `index.tsx`.
 * `apiBase` is where the API lives (the backend origin).
 */
export function startOfflineRuntime(apiBase: string): () => void {
  if (started) return () => undefined;
  started = true;

  void syncCredentials(apiBase).then(() => {
    void refreshOfflineState();
    void syncNow();
  });

  onOutboxChanged(() => {
    void refreshOfflineState();
  });

  const onOnline = () => {
    setOfflineState({ online: true });
    void syncNow();
    void requestBackgroundSync();
  };
  const onOffline = () => setOfflineState({ online: false });
  const onVisible = () => {
    if (document.visibilityState !== 'visible') return;
    void syncCredentials(apiBase);
    void refreshOfflineState();
    if (navigator.onLine !== false) void syncNow();
  };

  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOffline);
  document.addEventListener('visibilitychange', onVisible);

  let channel: BroadcastChannel | null = null;
  try {
    if (typeof BroadcastChannel !== 'undefined') {
      channel = new BroadcastChannel(CHANNEL_NAME);
      channel.onmessage = handleMessage;
    }
  } catch {
    channel = null;
  }

  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('message', handleMessage);
  }

  // Retry on a timer whenever anything is waiting. The store's `online` flag
  // only records what the last request did, so it must not gate the retry —
  // a connection that came back quietly is exactly the case to catch.
  pollTimer = setInterval(() => {
    if (getOfflineState().waiting > 0) void syncNow();
  }, POLL_MS);

  return () => {
    started = false;
    window.removeEventListener('online', onOnline);
    window.removeEventListener('offline', onOffline);
    document.removeEventListener('visibilitychange', onVisible);
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.removeEventListener('message', handleMessage);
    }
    channel?.close();
    if (pollTimer) clearInterval(pollTimer);
    onOutboxChanged(null);
  };
}
